/**
 * Diálogo de medición IA de una foto (modo automático + opciones avanzadas).
 *
 * Flujo por defecto: un solo botón "Medir estancia". La IA prioriza devolver
 * la SUPERFICIE DEL SUELO (m²); si no es viable, superficie de pared del fondo;
 * si tampoco, fondo lineal. Todo con objeto de mobiliario/arquitectónico auto-
 * detectado como referencia de escala.
 *
 * Opciones avanzadas (colapsables): permite al perito indicar objetivos
 * concretos y pistas.
 */
import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Ruler, Loader2, AlertTriangle, Sparkles, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const QUALITY_LABEL = {
  alta: { text: "Foto de alta calidad", color: "text-[#1D9E75]" },
  media: { text: "Calidad media", color: "text-[#B98A18]" },
  baja: { text: "Calidad baja", color: "text-[#B43F3F]" },
};

function fmtNum(n) {
  if (n == null) return "—";
  if (typeof n !== "number") return String(n);
  return n.toLocaleString("es-ES", { maximumFractionDigits: 2 });
}

export function PhotoMeasureDialog({ photo, open, onOpenChange, onSaved }) {
  const [targetsText, setTargetsText] = useState("");
  const [hint, setHint] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [result, setResult] = useState(photo?.measurements || null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (open) {
      setResult(photo?.measurements || null);
      setTargetsText((photo?.measurements?.targets || []).join("\n"));
      setHint(photo?.measurements?.perito_hint || "");
      setShowAdvanced(false);
    }
  }, [open, photo?.measurements, photo?.id]);

  const measure = async () => {
    setMeasuring(true);
    const startedAt = Date.now();
    const targets = targetsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 8);
    const hintClean = hint.trim() || null;

    // Fallback: si axios se corta antes de que la IA responda, el backend
    // igual persiste el resultado. Polleamos GET /photos/{id}/meta hasta 3 min.
    const pollForResult = async () => {
      const deadline = Date.now() + 180_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4_000));
        try {
          const m = await api.get(`/photos/${photo.id}/meta`);
          const meas = m.data?.measurements;
          const createdAt = meas?.created_at ? Date.parse(meas.created_at) : 0;
          if (meas && createdAt >= startedAt - 5_000) {
            return meas;
          }
        } catch (_) { /* ignorar y seguir polleando */ }
      }
      return null;
    };

    try {
      const r = await api.post(`/photos/${photo.id}/measure`, {
        targets,
        perito_hint: hintClean,
      }, { timeout: 180_000 });
      setResult(r.data?.measurements || null);
      toast.success("Medición IA completada");
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      if (!status || err?.code === "ECONNABORTED" || (err?.message || "").includes("Network Error")) {
        toast.info("La IA tarda más de lo normal — esperando resultado…", { duration: 6000 });
        const rescued = await pollForResult();
        if (rescued) {
          setResult(rescued);
          toast.success("Medición IA completada");
          onSaved?.();
        } else {
          toast.error("La IA no respondió a tiempo. Prueba de nuevo.");
        }
      } else if (status === 502) {
        toast.error("La IA no pudo interpretar la foto. Prueba con otra o añade pistas en opciones avanzadas.");
      } else {
        toast.error("No se pudo medir: " + (err?.response?.data?.detail || err.message));
      }
    } finally {
      setMeasuring(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("¿Borrar la medición actual?")) return;
    setClearing(true);
    try {
      await api.delete(`/photos/${photo.id}/measure`);
      setResult(null);
      toast.success("Medición eliminada");
      onSaved?.();
    } catch (err) {
      toast.error("No se pudo borrar: " + (err?.response?.data?.detail || err.message));
    } finally {
      setClearing(false);
    }
  };

  const quality = result?.calidad_foto ? QUALITY_LABEL[result.calidad_foto] : null;
  const primary = (result?.mediciones || [])[0];
  const secondary = (result?.mediciones || []).slice(1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-[16px] max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="photo-measure-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Ruler className="h-4 w-4 text-[#D85A30]" />
            Medir con IA
          </DialogTitle>
          <DialogDescription>
            La IA detecta automáticamente objetos con dimensiones estándar (muebles, azulejos, puertas, ventanas…) y estima la superficie de la estancia.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* HERO RESULTADO (arriba, siempre visible cuando hay medición) */}
          {result && primary && (
            <div className="bg-gradient-to-br from-[#FAECE7] to-[#FBFAF6] border border-[#F2D0C6] rounded-[14px] p-5" data-testid="measure-hero-result">
              <div className="text-[11px] uppercase tracking-wider text-[#7A2A14] font-semibold mb-1">
                {primary.objetivo || "Medición principal"}
              </div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl font-heading font-bold text-[#D85A30]" data-testid="measure-hero-value">
                  {fmtNum(primary.valor_principal)}
                </span>
                <span className="text-lg text-[#7A2A14] font-medium">{primary.unidad || ""}</span>
                {primary.incertidumbre_pct != null && (
                  <span className="text-sm text-[#B98A18] font-medium" data-testid="measure-hero-error">
                    ±{primary.incertidumbre_pct}%
                  </span>
                )}
              </div>
              {primary.dimensiones && (
                <div className="text-sm text-[#7A786F] mt-1">{primary.dimensiones}</div>
              )}
              {result.referencia_usada && (
                <div className="text-xs text-[#2E5AAC] mt-2">
                  <strong>Referencia:</strong> {result.referencia_usada}
                </div>
              )}
              {quality && (
                <div className={`text-[11px] font-medium mt-1 ${quality.color}`} data-testid="measure-quality">
                  {quality.text}
                </div>
              )}
              {primary.notas && (
                <div className="text-[11px] text-[#7A786F] mt-2 border-t border-[#F2D0C6] pt-2">{primary.notas}</div>
              )}
            </div>
          )}

          {/* MEDICIONES SECUNDARIAS */}
          {secondary.length > 0 && (
            <div className="space-y-1.5" data-testid="measure-secondary-list">
              <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold">
                Mediciones adicionales
              </div>
              {secondary.map((m, i) => (
                <div
                  key={i}
                  className="bg-white border border-[#E5E2D8] rounded-[10px] p-2.5 text-sm flex items-baseline gap-2 flex-wrap"
                >
                  <span className="text-[#2C2C2A] font-medium flex-1 min-w-0 truncate">{m.objetivo || `Medición ${i + 2}`}</span>
                  <span className="text-lg font-bold text-[#D85A30]">{fmtNum(m.valor_principal)}</span>
                  <span className="text-xs text-[#7A786F]">{m.unidad || ""}</span>
                  {m.incertidumbre_pct != null && (
                    <span className="text-[11px] text-[#B98A18]">±{m.incertidumbre_pct}%</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {result?.advertencias && (
            <div className="text-[11px] bg-[#FDF4E3] border border-[#F0D9A1] rounded-[10px] p-2.5 text-[#8B6914] flex gap-2">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <div>{result.advertencias}</div>
            </div>
          )}

          {result && (result.referencias_detectadas || []).length > 0 && (
            <details className="text-[11px] text-[#7A786F]">
              <summary className="cursor-pointer hover:text-[#2C2C2A]">
                Ver {result.referencias_detectadas.length} referencia(s) detectada(s)
              </summary>
              <ul className="mt-1.5 space-y-1 pl-3">
                {result.referencias_detectadas.map((r, i) => (
                  <li key={i}>
                    • <strong>{r.objeto}</strong>
                    {r.dimension_asumida_cm && ` — ${r.dimension_asumida_cm} cm`}
                    {r.confianza != null && <span className="text-[#B98A18]"> · conf. {r.confianza}%</span>}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {/* FOTO + BOTÓN PRINCIPAL */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3 items-center">
            <img
              src={`${API}/photos/${photo.id}`}
              alt={photo.filename}
              className="w-full h-24 sm:h-full object-cover rounded-[10px] border border-[#E5E2D8]"
            />
            <Button
              onClick={measure}
              disabled={measuring || clearing}
              className="w-full h-14 bg-[#D85A30] hover:bg-[#C04D27] text-white rounded-[12px] text-base font-semibold"
              data-testid="measure-run-btn"
            >
              {measuring ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Midiendo…</>
              ) : (
                <><Sparkles className="h-5 w-5 mr-2" /> {result ? "Volver a medir" : "Medir estancia"}</>
              )}
            </Button>
          </div>

          {result && !primary && (result.mediciones || []).length === 0 && (
            <div className="text-xs bg-[#FBEEEA] border border-[#F2D0C6] rounded-[10px] p-2.5 text-[#B43F3F]">
              La IA no ha podido devolver mediciones para esta foto. Prueba con otra foto donde se vea algún mueble, azulejo o puerta como referencia.
            </div>
          )}

          {/* OPCIONES AVANZADAS (colapsable, oculto por defecto) */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-xs text-[#7A786F] hover:text-[#2C2C2A] flex items-center gap-1"
              data-testid="measure-advanced-toggle"
            >
              {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              Opciones avanzadas (objetivos concretos, pistas)
            </button>
            {showAdvanced && (
              <div className="mt-2 space-y-3 border-t border-[#E5E2D8] pt-3">
                <div>
                  <label className="text-[11px] text-[#7A786F] font-medium">Objetivos concretos <span className="italic">(una por línea)</span></label>
                  <Textarea
                    value={targetsText}
                    onChange={(e) => setTargetsText(e.target.value)}
                    placeholder={"pared del fondo afectada\nsuperficie de suelo dañada\naltura del techo"}
                    maxLength={800}
                    rows={3}
                    className="text-xs rounded-[10px] border-[#E5E2D8] mt-0.5"
                    data-testid="measure-targets-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-[#7A786F] font-medium">Pistas del perito sobre la escena</label>
                  <Textarea
                    value={hint}
                    onChange={(e) => setHint(e.target.value)}
                    placeholder={"P. ej. la cama es una 105, el azulejo mide 45×45…"}
                    maxLength={800}
                    rows={2}
                    className="text-xs rounded-[10px] border-[#E5E2D8] mt-0.5"
                    data-testid="measure-hint-input"
                  />
                </div>
              </div>
            )}
          </div>

          {result && (
            <div className="text-[10px] text-[#7A786F] italic bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-2">
              ⚠️ Medida estimada por IA. No sustituye la medición métrica del perito. Márgenes típicos ±10-25%.
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {result && (
            <Button
              variant="outline"
              onClick={clear}
              disabled={clearing || measuring}
              className="rounded-[10px] border-[#E5E2D8] text-[#B43F3F]"
              data-testid="measure-clear-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Borrar medición
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={measuring}
            className="rounded-[10px] border-[#E5E2D8]"
            data-testid="measure-close-btn"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
